import { useState, useEffect, useCallback } from "react";
import { FileText, Download, ChevronRight, MapPin, Save, Loader } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function fmtEur(v) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(v || 0);
}

// ── Adres formulier ──────────────────────────────────────────────────────────
function AdresForm({ token, initialData, onSaved }) {
  const [form, setForm] = useState({
    adres: initialData?.adres || "",
    postcode: initialData?.postcode || "",
    stad: initialData?.stad || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/account/adres`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      setSaved(true);
      onSaved?.(form);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white/3 border border-white/8 rounded-xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <MapPin size={15} style={{ color: GOLD }} />
        <span className="text-white font-semibold text-sm">Factuuradres</span>
        <span className="text-zinc-500 text-xs ml-1">(verschijnt op je facturen)</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-3">
          <label className="block text-zinc-500 text-xs font-semibold uppercase tracking-wider mb-1">Straat + huisnummer</label>
          <input
            value={form.adres}
            onChange={e => setForm(f => ({ ...f, adres: e.target.value }))}
            placeholder="Voorbeeldstraat 12"
            data-testid="adres-straat"
            className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder-zinc-600"
          />
        </div>
        <div>
          <label className="block text-zinc-500 text-xs font-semibold uppercase tracking-wider mb-1">Postcode</label>
          <input
            value={form.postcode}
            onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))}
            placeholder="1234 AB"
            data-testid="adres-postcode"
            className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder-zinc-600"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-zinc-500 text-xs font-semibold uppercase tracking-wider mb-1">Stad</label>
          <input
            value={form.stad}
            onChange={e => setForm(f => ({ ...f, stad: e.target.value }))}
            placeholder="Amsterdam"
            data-testid="adres-stad"
            className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 transition-colors placeholder-zinc-600"
          />
        </div>
      </div>
      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="adres-opslaan-btn"
        className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
        style={{ background: saved ? "#22c55e" : GOLD, color: "#000" }}
      >
        {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
        {saved ? "Opgeslagen!" : saving ? "Opslaan..." : "Adres opslaan"}
      </button>
    </div>
  );
}

// ── Factuur rij ──────────────────────────────────────────────────────────────
function FactuurRij({ invoice, token }) {
  const [loading, setLoading] = useState(false);

  const downloadPdf = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/invoices/${invoice.invoice_number}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FitSmith-factuur-${invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`factuur-rij-${invoice.invoice_number}`}
      className="flex items-center justify-between p-4 bg-white/2 border border-white/6 rounded-xl hover:border-white/12 transition-all">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(201,168,76,0.12)" }}>
          <FileText size={16} style={{ color: GOLD }} />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">Factuur #{invoice.invoice_number}</p>
          <p className="text-zinc-500 text-xs mt-0.5">{invoice.product?.omschrijving}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right hidden sm:block">
          <p className="text-white font-bold text-sm">{fmtEur(invoice.totaal_incl_btw)}</p>
          <p className="text-zinc-600 text-xs">{fmt(invoice.datum)}</p>
        </div>
        <span className="hidden sm:inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-500/10 text-green-400 border border-green-500/20">
          Betaald
        </span>
        <button
          onClick={downloadPdf}
          disabled={loading}
          data-testid={`download-pdf-${invoice.invoice_number}`}
          title="Factuur downloaden"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105 disabled:opacity-50"
          style={{ background: GOLD, color: "#000" }}
        >
          {loading ? <Loader size={13} className="animate-spin" /> : <Download size={13} />}
          <span className="hidden sm:inline">PDF</span>
        </button>
      </div>
    </div>
  );
}

// ── Hoofd component ──────────────────────────────────────────────────────────
export default function MijnFacturenSection({ token, user }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/invoices/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="mijn-facturen-section">
      <div className="flex items-center gap-2 mb-5">
        <FileText size={18} style={{ color: GOLD }} />
        <h2 className="text-white font-bold text-xl">Mijn Facturen</h2>
      </div>

      <AdresForm token={token} initialData={user} onSaved={() => {}} />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: `${GOLD}30`, borderTopColor: GOLD }} />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 border border-white/6 rounded-xl bg-white/2">
          <FileText size={32} className="mx-auto mb-3 text-zinc-600" />
          <p className="text-zinc-400 font-semibold">Nog geen facturen</p>
          <p className="text-zinc-600 text-sm mt-1">Na een aankoop vind je hier je facturen.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <FactuurRij key={inv.invoice_number} invoice={inv} token={token} />
          ))}
        </div>
      )}
    </div>
  );
}
